import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useUser } from '../contexts/UserContext';
import { courtApi } from '../services/api';
import GiftIcon from './icons/GiftIcon';

const PaymentSuccess = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { refreshUserProfile } = useUser();
  const { paymentId } = useParams();
  const [paymentData, setPaymentData] = useState(null);
  const [topup, setTopup] = useState(null);
  const [refund, setRefund] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (paymentId) {
      fetchPaymentData();
    } else {
      setError('No payment ID provided');
      setLoading(false);
    }
  }, [paymentId]);

  const fetchPaymentData = async () => {
    try {
      setLoading(true);
      const result = await courtApi.getPayment(paymentId);
      setPaymentData(result);

      // Topup (F2): no booking/receipt — show "tokens added" and refresh balance.
      if (result?.topup) {
        await refreshUserProfile();
        setTopup({ creditTokens: result.creditTokens, newBalance: result.newBalance });
        setLoading(false);
        return;
      }

      // Paid but couldn't confirm the booking (slot taken, booking cancelled or
      // already paid): staff refund it by hand, so show the evidence to send them.
      if (result?.needsRefund) {
        setRefund(result);
        setLoading(false);
        return;
      }

      // Check if payment is actually successful
      if (result?.payment?.status === 'completed') {
        // Refresh user profile to update points
        await refreshUserProfile();
        
        // Redirect to receipt page after a short delay
        setTimeout(() => {
          navigate(`/receipt/${paymentId}`);
        }, 2000);
      } else {
        // Payment not completed - show error
        setError('Payment not completed');
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching payment:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-40 bg-gradient-to-br from-green-500 via-emerald-500 to-teal-500 p-5">
        <div className="flex flex-col items-center justify-center h-screen text-white">
          <div className="w-12 h-12 border-4 border-white border-opacity-30 border-t-white rounded-full animate-spin mb-5"></div>
          <p className="text-xl">{t('verifying_payment')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen pt-40 bg-gradient-to-br from-green-500 via-emerald-500 to-teal-500 p-5">
        <div className="flex flex-col items-center justify-center h-screen text-white text-center">
          <div className="bg-red-500 rounded-full p-4 mb-4">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold mb-4">{t('payment_failed')}</h2>
          <p className="text-lg mb-6">{error}</p>
          <div className="flex space-x-4">
            <button
              onClick={() => navigate('/')}
              className="bg-white bg-opacity-20 border-2 border-white text-white px-6 py-3 rounded-full text-lg font-medium transition-all duration-300 hover:bg-white hover:text-green-500"
            >
              {t('back_to_home')}
            </button>
            <button
              onClick={() => window.location.reload()}
              className="bg-white bg-opacity-20 border-2 border-white text-white px-6 py-3 rounded-full text-lg font-medium transition-all duration-300 hover:bg-white hover:text-green-500"
            >
              {t('try_again')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Topup success — no receipt, show tokens added + balance
  if (topup) {
    return (
      <div className="min-h-screen pt-40 bg-gradient-to-br from-green-500 via-emerald-500 to-teal-500 p-5">
        <div className="flex flex-col items-center justify-center h-screen text-white text-center">
          <GiftIcon className="w-24 h-24 mb-4 drop-shadow-lg" />
          <h1 className="text-2xl font-bold mb-2">{t('tokens_added') || 'Tokens added'}</h1>
          <p className="text-4xl font-extrabold mb-1">+{topup.creditTokens} {t('tokens')}</p>
          {topup.newBalance != null && (
            <p className="text-lg opacity-90 mb-8">{t('new_balance') || 'New balance'}: {Number(topup.newBalance).toFixed(1)} {t('tokens')}</p>
          )}
          <button
            onClick={() => navigate('/')}
            className="bg-white bg-opacity-20 border-2 border-white text-white px-6 py-3 rounded-full text-lg font-medium hover:bg-white hover:text-green-500 transition"
          >
            {t('back_to_home')}
          </button>
        </div>
      </div>
    );
  }

  // Paid but no booking confirmed — explain what happened and what to do, with the
  // references staff need to find the payment
  if (refund) {
    const { payment, booking } = refund;
    const refunded = refund.refundStatus === 'refunded';
    return (
      <div className="min-h-screen bg-gray-50 px-5 pt-40 pb-10">
        <div className="mx-auto max-w-md rounded-3xl bg-white p-6 shadow-sm">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 text-2xl">⚠️</div>
          <h1 className="text-center text-xl font-bold text-gray-900">{t(`refund_title_${refund.refundReason}`)}</h1>
          <p className="mt-2 text-center text-sm text-gray-600">
            {t(refunded ? 'refund_body_refunded' : refund.refundReason === 'already_paid' ? 'refund_body_already_paid' : 'refund_body')}
          </p>

          <dl className="mt-5 space-y-2 rounded-2xl bg-gray-50 p-4 text-sm">
            <div className="flex justify-between gap-3">
              <dt className="shrink-0 text-gray-500">{t(refunded ? 'refunded_amount' : 'refund_amount')}</dt>
              <dd className="font-semibold text-gray-900">฿{Number(payment.amount).toLocaleString()}</dd>
            </div>
            {booking && (
              <div className="flex justify-between gap-3">
                <dt className="shrink-0 text-gray-500">{t('refund_booking')}</dt>
                <dd className="text-right text-gray-900">{booking.courtName} · {booking.date} {booking.startTime}–{booking.endTime}</dd>
              </div>
            )}
            <div className="flex justify-between gap-3">
              <dt className="shrink-0 text-gray-500">{t('refund_ref')}</dt>
              <dd className="break-all text-right font-mono text-gray-900">{payment.id}</dd>
            </div>
            {payment.txId && (
              <div className="flex justify-between gap-3">
                <dt className="shrink-0 text-gray-500">{t('bank_ref')}</dt>
                <dd className="break-all text-right font-mono text-gray-900">{payment.txId}</dd>
              </div>
            )}
          </dl>

          <p className={`mt-4 rounded-xl px-3 py-2 text-center text-sm font-medium ${refunded ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-800'}`}>
            {refunded ? `✓ ${t('refund_status_refunded')}` : t('refund_status_due')}
          </p>

          <button
            onClick={() => navigate('/')}
            className="mt-6 w-full rounded-full bg-gray-900 py-3 text-base font-medium text-white"
          >
            {t('back_to_home')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-40 bg-gradient-to-br from-green-500 via-emerald-500 to-teal-500 p-5">
      <div className="flex flex-col items-center justify-center h-screen text-white text-center">
        {/* Success Display */}
        <div className="mb-8">
          <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold mb-2">{t('payment_successful')}</h1>
          <p className="text-lg opacity-90">{t('redirecting_to_receipt')}</p>
        </div>

        {/* Loading indicator */}
        <div className="w-12 h-12 border-4 border-white border-opacity-30 border-t-white rounded-full animate-spin"></div>
      </div>
    </div>
  );
};

export default PaymentSuccess; 